import {
  Id,
  Observer,
  Server,
  UserRole,
  UsersResponse,
  convertObserver,
} from '@nextforce/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import keys from './shared/keys'

export function useUserDetails(
  companyId?: Id,
  userId?: Id,
  role?: UserRole,
  enabled = true
) {
  const { server } = useServer()
  const client = useQueryClient()
  const cachedUserList = client.getQueryData<Observer[]>(
    keys.list(companyId, role)
  )
  const cachedUser = cachedUserList?.find(c => c.id === userId)

  return useQuery<Observer>({
    queryKey: keys.detail(companyId, userId),
    queryFn: () => fetchUserDetails(server, getUrl({ userId, role, companyId })),
    initialData: cachedUser,
    enabled: !!userId && !!role && !!companyId && enabled,
  })
}

export function useUserDetailsPhoneNumber(
  phoneNumber?: string,
  enabled = true
) {
  const { server } = useServer()

  return useQuery<Observer>({
    queryKey: keys.addUser(phoneNumber),
    queryFn: () => fetchUserDetails(server, getUrl({ phoneNumber })),
    refetchOnMount: 'always',
    enabled,
  })
}

async function fetchUserDetails(server: Server, url: string) {
  const response = await server.get<UsersResponse.Observer>(url)
  return convertObserver(response)
}

function getUrl({
  userId,
  phoneNumber,
  role,
  companyId,
}: {
  userId?: string
  phoneNumber?: string
  role?: UserRole
  companyId?: string
}) {
  let baseUrl = `/v2/users`
  if (userId) {
    if (role === 'observer') {
      baseUrl = `${baseUrl}/observers/${userId}`
    } else if (role === 'staff' || role === 'guest') {
      baseUrl = `${baseUrl}/${userId}`
    }
    if (companyId) {
      baseUrl = `${baseUrl}?company_id=${companyId}`
    }
  } else if (phoneNumber) {
    baseUrl = `${baseUrl}/mobile/${phoneNumber}`
  }
  return baseUrl
}
